import type { RadioStation } from "./types.js";

/** Codecs the MVP offers; actual decoding still depends on the browser. */
export const RADIO_CODECS: ReadonlySet<string> = new Set(["MP3", "AAC", "AAC+"]);

export const STATION_NAME_MAX = 80;
export const STATION_URL_MAX = 2048;
const TAG_MAX = 24;
const TAGS_MAX = 6;

/** Any RFC 4122 shaped UUID: Radio Browser uses several versions. */
export const STATION_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isStationId(value: unknown): value is string {
  return typeof value === "string" && STATION_ID_RE.test(value);
}

const INVISIBLE_RE = /[​-‏‪-‮⁠⁦-⁩﻿]/g;
const CONTROL_RE = /[\u0000-\u001F\u007F-\u009F]/g;

function cleanText(value: string, max: number): string {
  const text = value
    .normalize("NFKC")
    .replace(INVISIBLE_RE, "")
    .replace(CONTROL_RE, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

/** An `https:` URL within the length cap, or null. */
function httpsUrl(value: unknown): string | null {
  if (typeof value !== "string" || value.length === 0 || value.length > STATION_URL_MAX) {
    return null;
  }
  try {
    const url = new URL(value.trim());
    return url.protocol === "https:" ? url.href : null;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Validate one untrusted Radio Browser station object. Returns null for
 * anything the game can't offer: missing fields, non-HTTPS or HLS streams,
 * stations failing their last check, or unsupported codecs.
 */
export function normalizeStation(raw: unknown): RadioStation | null {
  if (!isRecord(raw)) return null;
  const { stationuuid, name, url_resolved, codec, hls, lastcheckok } = raw;
  if (!isStationId(stationuuid)) return null;
  if (typeof name !== "string" || typeof codec !== "string") return null;
  if (lastcheckok !== 1 || hls !== 0) return null;

  const displayName = cleanText(name, STATION_NAME_MAX);
  if (!displayName) return null;
  const streamUrl = httpsUrl(url_resolved);
  if (!streamUrl) return null;
  const normalizedCodec = codec.trim().toUpperCase();
  if (!RADIO_CODECS.has(normalizedCodec)) return null;

  const tags =
    typeof raw.tags === "string"
      ? raw.tags
          .split(",")
          .map((tag) => cleanText(tag, TAG_MAX))
          .filter((tag) => tag.length > 0)
          .slice(0, TAGS_MAX)
      : [];
  const countryCode =
    typeof raw.countrycode === "string" && /^[A-Z]{2}$/i.test(raw.countrycode.trim())
      ? raw.countrycode.trim().toUpperCase()
      : null;
  const bitrate = Number(raw.bitrate);

  return {
    id: stationuuid.toLowerCase(),
    name: displayName,
    streamUrl,
    logoUrl: httpsUrl(raw.favicon),
    countryCode,
    tags,
    codec: normalizedCodec,
    bitrateKbps: Number.isFinite(bitrate) && bitrate > 0 ? Math.round(bitrate) : null,
  };
}

/**
 * Normalize a directory response, dropping invalid entries and duplicate
 * UUIDs (including ones already in `seen`, which is updated).
 */
export function normalizeStations(raw: unknown, seen: Set<string> = new Set()): RadioStation[] {
  if (!Array.isArray(raw)) return [];
  const out: RadioStation[] = [];
  for (const item of raw) {
    const station = normalizeStation(item);
    if (!station || seen.has(station.id)) continue;
    seen.add(station.id);
    out.push(station);
  }
  return out;
}
